import React, { CSSProperties, useState } from 'react'
import type {
    WebformButton,
    WebformFieldLabels,
    WebformNameField,
    WebformContactIcon,
    WebformContactItem,
    WebformCustomQuestion,
    WebformFonts,
    WebformHeader,
    WebformHeaderLevel,
    WebformMaxWidth,
    WebformLabelStyle,
    WebformLayout,
} from './types'
import { splitFullName } from './name'
import {
    inkOn,
    parseHex,
    readableOn,
    resolveFonts,
    resolveHeaders,
    resolveHeaderSize,
    resolveHeaderStack,
    resolveHeaderWeight,
    resolveMaxWidth,
    surfacePalette,
    type ResolvedFonts,
    type SurfacePalette,
} from './theme'

export type { WebformCustomQuestion } from './types'

// Single-source-of-truth renderer for the embeddable webform.
//
// Used in two places:
//   1. The admin edit dialog (live preview pane).
//   2. The slice-2 embed bundle (mounted into a sandboxed iframe on clinic sites).
//
// Constraints, so both consumers get the same visual:
//   - No Tailwind, no shadcn, no app design tokens — inline styles only.
//   - Self-contained: only depends on React + the WebformCustomQuestion type.
//   - Submission is opt-in via onSubmit; admin preview leaves it as a no-op.

export type WebformRendererProps = {
    title: string
    subtitle?: string | null
    // Configurable header block. When empty, falls back to title + subtitle so
    // configs predating this field render exactly as before.
    headers?: WebformHeader[]
    primaryColour: string
    // Page background behind the form.
    secondaryColour: string
    // Buttons and interactive marks. Falls back to primaryColour when unset, so
    // a two-colour config renders exactly as it did before accent existed.
    accentColour?: string | null
    // Submit button background. Falls back to accentColour (and so to
    // primaryColour) when unset, which is how the button has always been coloured.
    buttonColour?: string | null
    // Submit button label. Unset keeps white, the colour the button has always used.
    buttonTextColour?: string | null
    // Caps the form's width and centres it in whatever container the embed sits
    // in. Unset fills the container.
    maxWidth?: WebformMaxWidth | null
    // Background of the panel holding the fields. Falls back to secondaryColour,
    // which is the single-surface form every config rendered as before.
    formPanelColour?: string | null
    // 'split' puts the header block, contact lines and buttons in their own
    // column beside the fields. Defaults to the original single panel.
    layout?: WebformLayout
    // 'placeholder' moves each field's name inside its box.
    labelStyle?: WebformLabelStyle
    // 'single' replaces the first/last pair with one "Name" box.
    nameField?: WebformNameField
    // Overrides for the built-in field names, e.g. "Phone" for Mobile.
    fieldLabels?: WebformFieldLabels
    // Overrides the submit button's text ("Send", "Request a callback"…).
    submitLabel?: string | null
    // Address / phone / email lines shown in the contact column.
    contactItems?: WebformContactItem[]
    // Call-to-action links (Book Here, WhatsApp). Never submit the form.
    buttons?: WebformButton[]
    // Per-role fonts (heading / label / body). Any role left unset uses the
    // standard system stack.
    fonts?: WebformFonts
    privacyPolicyLink?: string | null
    customQuestions: WebformCustomQuestion[]
    // When omitted, the form is preview-only — the submit button is disabled.
    onSubmit?: (answers: WebformAnswers) => Promise<void> | void
}

export type WebformAnswers = {
    firstName: string
    lastName: string
    email: string
    mobile: string
    customAnswers: Record<string, string | boolean>
    acceptedPrivacyPolicy: boolean
    // Hidden honeypot — populated by naive bots, ignored by real users.
    honeypot: string
}

const EMPTY_ANSWERS: WebformAnswers = {
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    customAnswers: {},
    acceptedPrivacyPolicy: false,
    honeypot: '',
}

// Visually hides the honeypot field from sighted users and screen readers
// while remaining in the DOM for naive bots to discover and auto-fill.
const HONEYPOT_STYLE: CSSProperties = {
    position: 'absolute',
    left: '-10000px',
    top: 'auto',
    width: 1,
    height: 1,
    overflow: 'hidden',
    opacity: 0,
}

// Same trick, but for a real label: the field name stays in the accessibility
// tree (and so keeps driving autofill) while only the placeholder is painted.
const VISUALLY_HIDDEN: CSSProperties = {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
}

const INPUT_CLASS = 'cw-field'
const REQUIRED_MARK_COLOUR = '#c0392b'

// Embed bundle runs inside a sandboxed iframe on clinics' sites; defensively
// strip non-http(s) hrefs (e.g. javascript:) regardless of upstream validation.
function safeHref(url: string | null | undefined): string | undefined {
    if (!url) return undefined
    try {
        const parsed = new URL(url)
        if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
            return parsed.toString()
        }
    } catch {
        // fall through
    }
    return undefined
}

// The greyed-out background a disabled button has always used.
const DISABLED_BUTTON_BACKGROUND = '#a4a9b0'

// Colours reach the stylesheet below, not just the style attribute, so anything
// interpolated there has to be re-derived from parsed channels rather than
// echoed through as config text.
function cssColour(value: string | null | undefined, fallback: string): string {
    const rgb = parseHex(value)
    if (!rgb) return fallback
    return `rgb(${rgb.r},${rgb.g},${rgb.b})`
}

function questionKey(q: WebformCustomQuestion, i: number): string {
    return q.id ?? `preview-${i}`
}

export function WebformRenderer({
    title,
    subtitle,
    headers,
    primaryColour,
    secondaryColour,
    accentColour,
    buttonColour,
    buttonTextColour,
    maxWidth,
    formPanelColour,
    layout = 'form',
    labelStyle = 'above',
    nameField = 'split',
    fieldLabels,
    submitLabel,
    contactItems = [],
    buttons = [],
    fonts,
    privacyPolicyLink,
    customQuestions,
    onSubmit,
}: WebformRendererProps): React.ReactElement {
    const [answers, setAnswers] = useState<WebformAnswers>(EMPTY_ANSWERS)
    // Single-name mode keeps what was typed verbatim and only splits it on
    // submit, so the box never reformats itself under the patient's cursor.
    const [fullName, setFullName] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const previewMode = !onSubmit

    const updateCustom = (id: string, value: string | boolean) => {
        setAnswers(a => ({ ...a, customAnswers: { ...a.customAnswers, [id]: value } }))
    }

    const singleName = nameField === 'single'

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!onSubmit || submitting) return
        setSubmitting(true)
        try {
            await onSubmit(singleName ? { ...answers, ...splitFullName(fullName) } : answers)
        } finally {
            setSubmitting(false)
        }
    }

    // A one-word entry is allowed through with an empty last name; the service
    // decides whether that is acceptable for this form's name mode.
    const labelFor = (key: keyof NonNullable<typeof fieldLabels>, fallback: string) =>
        fieldLabels?.[key]?.trim() || fallback

    const requiresPrivacyTick = !!privacyPolicyLink
    const privacyHref = safeHref(privacyPolicyLink)
    const requiredsAnswered = customQuestions.every((q, i) =>
        !q.required || hasAnswer(answers.customAnswers[questionKey(q, i)], q.type),
    )
    const nameAnswered = singleName
        ? !!fullName.trim()
        : !!answers.firstName.trim() && !!answers.lastName.trim()
    const canSubmit = !previewMode
        && nameAnswered
        && !!answers.email.trim() && !!answers.mobile.trim()
        && (!requiresPrivacyTick || answers.acceptedPrivacyPolicy)
        && requiredsAnswered

    const resolvedHeaders = resolveHeaders(headers, title, subtitle)
    const resolvedFonts = resolveFonts(fonts, resolvedHeaders)
    const accent = accentColour || primaryColour
    const button = buttonColour || accent
    const split = layout === 'split'
    // Left unset the fields sit on the page background — one surface, exactly
    // as every config rendered before the panel colour existed.
    const panelColour = formPanelColour || secondaryColour
    const styles = buildStyles({
        primary: primaryColour,
        page: secondaryColour,
        panel: panelColour,
        accent,
        button,
        // An explicit choice still wins; otherwise the label colour is
        // derived from the button it sits on, so a pale button reads.
        buttonText: buttonTextColour || inkOn(button),
        maxWidth: resolveMaxWidth(maxWidth),
        fonts: resolvedFonts,
        labelStyle,
    })
    const buttonEnabled = canSubmit && !submitting
    // Only the field column's label style matters for placeholders.
    const placeholderFor = (label: string) => labelStyle === 'placeholder' ? label : undefined

    const headerBlock = resolvedHeaders.map((header, i) => (
        <HeaderLine
            key={header.id || `header-${i}`}
            header={header}
            last={i === resolvedHeaders.length - 1}
            fontStack={resolveHeaderStack(header, resolvedFonts)}
            styles={styles}
            surface={split ? styles.pagePalette : styles.panelPalette}
            background={split ? secondaryColour : panelColour}
        />
    ))

    const asideBlock = (
        <>
            {/* Contact lines belong to the contact column: the editor only offers
                them in split layout, so rendering them in the single panel would
                show a clinic lines they can no longer see or edit. Buttons are
                offered in both layouts and stay in both. */}
            {split && contactItems.length > 0 && (
                <ul style={styles.contactList}>
                    {contactItems.map((item, i) => (
                        <ContactLine key={item.id || `contact-${i}`} item={item} styles={styles} />
                    ))}
                </ul>
            )}
            {buttons.length > 0 && (
                <div style={styles.buttonRow}>
                    {buttons.map((button, i) => (
                        <CtaButton
                            key={button.id || `button-${i}`}
                            button={button}
                            fallbackColour={accentColour || primaryColour}
                            styles={styles}
                        />
                    ))}
                </div>
            )}
        </>
    )

    const fieldBlock = (
        <>
            {singleName ? (
                <Field label={labelFor('name', 'Name')} required styles={styles}>
                    <input
                        type="text"
                        className={INPUT_CLASS}
                        placeholder={placeholderFor(labelFor('name', 'Name'))}
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        style={styles.input}
                        autoComplete="name"
                    />
                </Field>
            ) : (
                <>
                    <Field label={labelFor('firstName', 'First name')} required styles={styles}>
                        <input
                            type="text"
                            className={INPUT_CLASS}
                            placeholder={placeholderFor(labelFor('firstName', 'First name'))}
                            value={answers.firstName}
                            onChange={(e) => setAnswers(a => ({ ...a, firstName: e.target.value }))}
                            style={styles.input}
                            autoComplete="given-name"
                        />
                    </Field>
                    <Field label={labelFor('lastName', 'Last name')} required styles={styles}>
                        <input
                            type="text"
                            className={INPUT_CLASS}
                            placeholder={placeholderFor(labelFor('lastName', 'Last name'))}
                            value={answers.lastName}
                            onChange={(e) => setAnswers(a => ({ ...a, lastName: e.target.value }))}
                            style={styles.input}
                            autoComplete="family-name"
                        />
                    </Field>
                </>
            )}
            <Field label={labelFor('email', 'Email')} required styles={styles}>
                <input
                    type="email"
                    className={INPUT_CLASS}
                    placeholder={placeholderFor(labelFor('email', 'Email'))}
                    value={answers.email}
                    onChange={(e) => setAnswers(a => ({ ...a, email: e.target.value }))}
                    style={styles.input}
                    autoComplete="email"
                />
            </Field>
            <Field label={labelFor('mobile', 'Mobile')} required styles={styles}>
                <input
                    type="tel"
                    className={INPUT_CLASS}
                    placeholder={placeholderFor(labelFor('mobile', 'Mobile'))}
                    value={answers.mobile}
                    onChange={(e) => setAnswers(a => ({ ...a, mobile: e.target.value }))}
                    style={styles.input}
                    autoComplete="tel"
                />
            </Field>

            {customQuestions.map((q, i) => {
                const key = questionKey(q, i)
                return (
                    <CustomQuestion
                        key={key}
                        question={q}
                        value={answers.customAnswers[key]}
                        onChange={(v) => updateCustom(key, v)}
                        placeholder={placeholderFor(q.label)}
                        styles={styles}
                    />
                )
            })}

            {requiresPrivacyTick && (
                <label style={styles.privacyLabel}>
                    <input
                        type="checkbox"
                        checked={answers.acceptedPrivacyPolicy}
                        onChange={(e) => setAnswers(a => ({ ...a, acceptedPrivacyPolicy: e.target.checked }))}
                        style={styles.checkbox}
                    />
                    <span>
                        I agree to the{' '}
                        {privacyHref ? (
                            <a href={privacyHref} target="_blank" rel="noreferrer" style={styles.link}>
                                privacy policy
                            </a>
                        ) : (
                            <span style={styles.link}>privacy policy</span>
                        )}.
                    </span>
                </label>
            )}

            <div aria-hidden="true" style={HONEYPOT_STYLE}>
                <label>
                    Leave this field blank
                    <input
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={answers.honeypot}
                        onChange={(e) => setAnswers(a => ({ ...a, honeypot: e.target.value }))}
                    />
                </label>
            </div>

            {/* The preview can never submit, but a clinic still has to see the
                button colour they picked — a permanently grey button makes that
                colour impossible to choose. So it is painted as enabled while
                staying genuinely disabled. */}
            <button
                type="submit"
                disabled={!buttonEnabled}
                style={styles.submit(buttonEnabled || previewMode, buttonEnabled)}
            >
                {submitting ? 'Sending…' : (submitLabel?.trim() || 'Submit')}
            </button>
        </>
    )

    // Built from curated URLs, a validated font name + https font-file URL, or
    // colours re-derived from parsed hex channels (see cssColour) — never raw
    // config text.
    const styleTag = <style>{resolvedFonts.fontFaceCss}{styles.css}</style>

    if (!split) {
        return (
            <form onSubmit={handleSubmit} style={styles.form}>
                {styleTag}
                {headerBlock}
                {asideBlock}
                {fieldBlock}
            </form>
        )
    }

    return (
        <div style={styles.splitShell}>
            {styleTag}
            {/* auto-fit columns rather than a media query: the embed and the
                admin preview pane are different widths, and this stacks on
                whichever of them is narrow without either knowing the other. */}
            <section style={styles.contactColumn}>
                {headerBlock}
                {asideBlock}
            </section>
            <form onSubmit={handleSubmit} style={styles.formColumn}>
                {fieldBlock}
            </form>
        </div>
    )
}

function HeaderLine({
    header, last, fontStack, styles, surface, background,
}: {
    header: WebformHeader
    // The last line carries the gap down to the first field; the rest sit tight
    // together so a multi-line header block reads as one unit.
    last: boolean
    fontStack: string
    styles: ReturnType<typeof buildStyles>
    surface: SurfacePalette
    background: string
}) {
    const style: CSSProperties = {
        ...styles.header(header.level, resolveHeaderWeight(header), resolveHeaderSize(header), surface, background),
        fontFamily: fontStack,
        textAlign: header.align,
        marginBottom: last ? 16 : 6,
    }
    if (header.level === 'body') return <p style={style}>{header.text}</p>
    const Tag = header.level as 'h1' | 'h2' | 'h3'
    return <Tag style={style}>{header.text}</Tag>
}

function ContactLine({
    item, styles,
}: {
    item: WebformContactItem
    styles: ReturnType<typeof buildStyles>
}) {
    const href = safeHref(item.linkUrl)
    const linkText = item.linkText?.trim()
    return (
        <li style={styles.contactItem}>
            <span style={styles.contactIcon} aria-hidden="true">
                <ContactIcon icon={item.icon} />
            </span>
            <span style={styles.contactText}>
                {item.label?.trim() && <strong style={styles.contactLabel}>{item.label.trim()} </strong>}
                {item.text}
                {linkText && (
                    <span style={styles.contactLinkRow}>
                        {href ? (
                            <a href={href} target="_blank" rel="noreferrer" style={styles.contactLink}>
                                {linkText} <ChevronIcon />
                            </a>
                        ) : (
                            <span style={styles.contactLink}>{linkText}</span>
                        )}
                    </span>
                )}
            </span>
        </li>
    )
}

function CtaButton({
    button, fallbackColour, styles,
}: {
    button: WebformButton
    fallbackColour: string
    styles: ReturnType<typeof buildStyles>
}) {
    const href = safeHref(button.url)
    const background = cssColour(button.colour, cssColour(fallbackColour, '#111111'))
    const style: CSSProperties = { ...styles.ctaButton, background, color: inkOn(button.colour || fallbackColour) }
    // A CTA with no usable URL still renders — the clinic can see it in the
    // preview while they are mid-edit — but it is inert rather than a dead link.
    if (!href) return <span style={{ ...style, cursor: 'default' }}>{button.text}</span>
    return (
        <a href={href} target="_blank" rel="noreferrer noopener" style={style}>
            {button.icon === 'whatsapp' && <WhatsappIcon />}
            {button.text}
        </a>
    )
}

// Wrap the control in the label so the association is implicit (no id/htmlFor
// plumbing needed) and clicking the label focuses the first control.
function Field({
    label, required, children, styles,
}: {
    label: string
    required?: boolean
    children: React.ReactNode
    styles: ReturnType<typeof buildStyles>
}) {
    return (
        <label style={{ display: 'block', marginBottom: 14 }}>
            <span style={styles.fieldLabel}>
                {label}{required && <span style={{ color: REQUIRED_MARK_COLOUR, marginLeft: 2 }}>*</span>}
            </span>
            {children}
        </label>
    )
}

function CustomQuestion({
    question, value, onChange, placeholder, styles,
}: {
    question: WebformCustomQuestion
    value: string | boolean | undefined
    onChange: (v: string | boolean) => void
    placeholder?: string
    styles: ReturnType<typeof buildStyles>
}) {
    if (question.type === 'checkbox') {
        return (
            <label style={{ ...styles.privacyLabel, marginBottom: 14 }}>
                <input
                    type="checkbox"
                    checked={!!value}
                    onChange={(e) => onChange(e.target.checked)}
                    style={styles.checkbox}
                />
                <span>{question.label}{question.required && <span style={{ color: REQUIRED_MARK_COLOUR, marginLeft: 2 }}>*</span>}</span>
            </label>
        )
    }
    return (
        <Field label={question.label} required={question.required} styles={styles}>
            {question.type === 'long_text' ? (
                <textarea
                    className={INPUT_CLASS}
                    placeholder={placeholder}
                    value={typeof value === 'string' ? value : ''}
                    onChange={(e) => onChange(e.target.value)}
                    style={{ ...styles.input, minHeight: 80, resize: 'vertical' as const }}
                    rows={3}
                />
            ) : question.type === 'dropdown' ? (
                <select
                    className={INPUT_CLASS}
                    value={typeof value === 'string' ? value : ''}
                    onChange={(e) => onChange(e.target.value)}
                    style={styles.input}
                >
                    <option value="">{placeholder ? `— ${placeholder} —` : '— Select —'}</option>
                    {question.options.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </select>
            ) : (
                <input
                    type="text"
                    className={INPUT_CLASS}
                    placeholder={placeholder}
                    value={typeof value === 'string' ? value : ''}
                    onChange={(e) => onChange(e.target.value)}
                    style={styles.input}
                />
            )}
        </Field>
    )
}

function hasAnswer(value: string | boolean | undefined, type: WebformCustomQuestion['type']): boolean {
    if (type === 'checkbox') return value === true
    return typeof value === 'string' && value.trim().length > 0
}

// Icons are inline so the embed stays a single bundle with no asset requests
// from the clinic's page. currentColor throughout, so they inherit the ink of
// whatever surface they land on.
function ContactIcon({ icon }: { icon: WebformContactIcon }): React.ReactElement {
    const common = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'currentColor' } as const
    if (icon === 'phone') {
        return (
            <svg {...common}>
                <path d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.24 11.4 11.4 0 0 0 3.6.58 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1 11.4 11.4 0 0 0 .57 3.6 1 1 0 0 1-.25 1z" />
            </svg>
        )
    }
    if (icon === 'email') {
        return (
            <svg {...common}>
                <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zm8 7.2L20 6.4V6H4v.4z" />
            </svg>
        )
    }
    if (icon === 'clock') {
        return (
            <svg {...common}>
                <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm1 10.6V6h-2v7.4l5.2 3.1 1-1.7z" />
            </svg>
        )
    }
    return (
        <svg {...common}>
            <path d="M12 2a7 7 0 0 0-7 7c0 5.2 7 13 7 13s7-7.8 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z" />
        </svg>
    )
}

function ChevronIcon(): React.ReactElement {
    return (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true" style={{ verticalAlign: 'middle' }}>
            <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    )
}

function WhatsappIcon(): React.ReactElement {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ marginRight: 8, verticalAlign: '-2px' }}>
            <path d="M12 2a10 10 0 0 0-8.6 15l-1.3 4.7 4.8-1.3A10 10 0 1 0 12 2zm5.8 14.1c-.2.7-1.2 1.3-2 1.4-.5.1-1.2.1-3.4-.8-2.9-1.2-4.7-4.1-4.8-4.3-.2-.2-1.2-1.5-1.2-2.9s.7-2 1-2.3c.2-.2.5-.3.7-.3h.5c.2 0 .4 0 .6.5l.8 2c.1.2 0 .4-.1.5l-.3.4c-.1.2-.3.3-.1.6.2.3.7 1.2 1.6 2 1.1.9 2 1.2 2.3 1.4.2.1.4.1.5-.1l.8-.9c.2-.2.3-.2.6-.1l1.9.9c.3.1.4.2.5.3s.1.6-.1 1.2z" />
        </svg>
    )
}

// Role mapping: headings use `heading`; field labels, checkbox copy and the
// submit button use `label`; everything else — body header lines, the inputs
// the patient types into — uses `body`.
type StyleInput = {
    primary: string
    page: string
    panel: string
    accent: string
    button: string
    buttonText: string
    maxWidth: number | null
    fonts: ResolvedFonts
    labelStyle: WebformLabelStyle
}

function buildStyles({
    primary, page, panel, accent, button, buttonText, maxWidth, fonts, labelStyle,
}: StyleInput) {
    const pagePalette = surfacePalette(page)
    const panelPalette = surfacePalette(panel)
    const inputBase: CSSProperties = {
        width: '100%',
        padding: labelStyle === 'placeholder' ? '13px 15px' : '9px 11px',
        fontSize: 14,
        fontFamily: fonts.body,
        border: `1px solid ${panelPalette.border}`,
        borderRadius: labelStyle === 'placeholder' ? 8 : 6,
        background: panelPalette.inputBackground,
        color: panelPalette.inputInk,
        boxSizing: 'border-box' as const,
        outline: 'none',
    }
    const accentCss = cssColour(accent, '#111111')
    return {
        pagePalette,
        panelPalette,
        // Rules inline styles can't express. Every colour here is re-derived
        // from parsed channels or a palette constant, never raw config text.
        css: [
            `.${INPUT_CLASS}::placeholder{color:${panelPalette.inputPlaceholder};opacity:1;}`,
            `.${INPUT_CLASS}:focus{border-color:${accentCss};}`,
            // Native dropdown popups are painted by the OS on a light sheet, so
            // white-on-transparent options would vanish inside one.
            `.${INPUT_CLASS} option{color:#222;background:#fff;}`,
        ].join(''),
        form: {
            fontFamily: fonts.body,
            background: panel,
            color: panelPalette.strong,
            padding: 20,
            borderRadius: 10,
            border: `1px solid ${panelPalette.dark ? 'rgba(255,255,255,0.12)' : '#e5e7eb'}`,
            // Centred inside the embed container rather than left-aligned, so a
            // narrow form on a wide page sits where a reader expects it.
            maxWidth: maxWidth ?? undefined,
            marginLeft: 'auto',
            marginRight: 'auto',
            boxSizing: 'border-box' as const,
        } as CSSProperties,
        // Flex wrap rather than a breakpoint: the two columns hold their
        // weighting while they fit and drop to stacked when they don't, in both
        // the embed and the narrower admin preview — no media query, so neither
        // has to know the other's width.
        splitShell: {
            display: 'flex',
            flexWrap: 'wrap' as const,
            alignItems: 'stretch',
            fontFamily: fonts.body,
            borderRadius: 12,
            overflow: 'hidden',
            // Same width cap and centring as the single-panel form, applied to
            // the whole two-column section.
            maxWidth: maxWidth ?? undefined,
            marginLeft: 'auto',
            marginRight: 'auto',
            boxSizing: 'border-box' as const,
            border: `1px solid ${pagePalette.dark ? 'rgba(255,255,255,0.12)' : '#e5e7eb'}`,
        } as CSSProperties,
        // The contact side carries full postal addresses, so it gets the wider
        // share; the fields need less room than two columns of address text.
        contactColumn: {
            flex: '1.3 1 320px',
            background: page,
            color: pagePalette.strong,
            padding: '36px 32px',
        } as CSSProperties,
        formColumn: {
            flex: '1 1 300px',
            background: panel,
            color: panelPalette.strong,
            padding: '36px 32px',
        } as CSSProperties,
        header: (
            level: WebformHeaderLevel,
            weight: number,
            size: number,
            surface: SurfacePalette,
            background: string,
        ): CSSProperties => ({
            fontSize: size,
            fontWeight: weight,
            fontFamily: level === 'body' ? fonts.body : fonts.heading,
            marginTop: 0,
            // A heading colour chosen against a light page would disappear on a
            // dark panel; fall back to the surface's own ink when it would.
            color: level === 'body' ? surface.muted : readableOn(primary, background, surface),
        }),
        contactList: {
            listStyle: 'none',
            margin: '0 0 22px',
            padding: 0,
            display: 'flex',
            flexDirection: 'column' as const,
            gap: 16,
        } as CSSProperties,
        contactItem: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            fontSize: 14,
            lineHeight: 1.5,
            fontFamily: fonts.body,
            color: pagePalette.strong,
        } as CSSProperties,
        contactIcon: {
            flexShrink: 0,
            marginTop: 2,
            color: pagePalette.strong,
            display: 'inline-flex',
        } as CSSProperties,
        contactText: { flex: 1 } as CSSProperties,
        contactLabel: { fontWeight: 700, fontFamily: fonts.label } as CSSProperties,
        contactLinkRow: { display: 'block', marginTop: 4 } as CSSProperties,
        contactLink: {
            color: pagePalette.strong,
            fontWeight: 600,
            fontFamily: fonts.label,
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
        } as CSSProperties,
        buttonRow: {
            display: 'flex',
            flexWrap: 'wrap' as const,
            gap: 12,
            marginBottom: 4,
        } as CSSProperties,
        ctaButton: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '13px 26px',
            fontSize: 14,
            fontWeight: 700,
            fontFamily: fonts.label,
            borderRadius: 999,
            textDecoration: 'none',
            cursor: 'pointer',
        } as CSSProperties,
        fieldLabel: {
            // The placeholder carries the name instead, but the label stays in
            // the DOM so screen readers and autofill still see it.
            ...(labelStyle === 'placeholder' ? VISUALLY_HIDDEN : {
                display: 'block',
                marginBottom: 6,
            }),
            fontSize: 13,
            fontWeight: 500,
            fontFamily: fonts.label,
            color: panelPalette.strong,
        } as CSSProperties,
        input: inputBase,
        privacyLabel: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            fontSize: 13,
            fontFamily: fonts.label,
            color: panelPalette.strong,
            marginTop: 4,
            marginBottom: 14,
            cursor: 'pointer',
        } as CSSProperties,
        checkbox: {
            marginTop: 2,
            accentColor: accent,
        } as CSSProperties,
        link: {
            color: readableOn(primary, panel, panelPalette),
            textDecoration: 'underline',
        } as CSSProperties,
        submit: (coloured: boolean, interactive: boolean = coloured): CSSProperties => ({
            width: '100%',
            padding: '13px 14px',
            fontSize: 15,
            fontWeight: 600,
            fontFamily: fonts.label,
            color: coloured ? buttonText : inkOn(DISABLED_BUTTON_BACKGROUND),
            background: coloured ? button : DISABLED_BUTTON_BACKGROUND,
            border: 'none',
            borderRadius: 6,
            cursor: interactive ? 'pointer' : 'not-allowed',
            marginTop: 6,
        }),
    }
}
